import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Glass, type GlassOptics } from '@samasante/liquid-glass';
import { cn } from '@/lib/utils';

const controlOptics: Partial<GlassOptics> = {
  mapSize: 96,
  clipToShape: true,
  softEdge: true,
  depth: 0.56,
  curvature: 0.2,
  dispersion: 0.04,
  strength: 0.036,
  bend: 0.26,
  bendWidth: 0.09,
  frost: 1.4,
  brightness: 0.07,
  specular: 0.72,
  sheenAngle: 50,
  sheen: 0.5,
  sheenWidth: 1.5,
  glow: 0.1,
  glowSpread: 0.58,
  glowFalloff: 1,
};

export default function TicketInfoGlass({ children }: { children: ReactNode }) {
  return <div className="ticket-info-clean-stage">{children}</div>;
}

export function TicketInfoLiquidControl({
  children,
  tone = 'neutral',
  className,
}: {
  children: ReactNode;
  tone?: 'neutral' | 'warm' | 'cool';
  className?: string;
}) {
  const hostRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useLayoutEffect(() => {
    const node = hostRef.current;
    if (!node) return;
    const measure = () => setSize({ width: node.clientWidth, height: node.clientHeight });
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={hostRef}
      className={cn('ticket-info-liquid-control', className)}
    >
      {size.width > 0 && size.height > 0 && (
        <Glass
          refract={(
            <div
              className={cn('ticket-info-control-source', `ticket-info-control-source--${tone}`)}
              style={{ width: size.width, height: size.height }}
            />
          )}
          behind="#f7f8fa"
          optics={controlOptics}
          width={size.width}
          height={size.height}
          filterResolution={1}
          brightnessInFilter
          radius={9}
          className="ticket-info-control-lens"
          style={{ position: 'absolute', inset: 0, width: size.width, height: size.height, borderRadius: 9 }}
        />
      )}
      <div className="ticket-info-liquid-control-content">{children}</div>
    </div>
  );
}
